import React, { useContext, useEffect, useState } from 'react'

import { TabContainer, TabContent, TabList } from 'components/shared/tabs'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from 'components/ui/accordion'
import { Button } from 'components/ui/button'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  Dialog as DialogRoot,
  DialogTitle
} from 'components/ui/dialog'
import { Switch } from 'components/ui/switch'
import { usePageConfig } from 'context/page-config-context'
import PortalConfigContext from 'context/PortalConfigContext'
import useEcpTranslation from 'hooks/common/useEcpTranslation'
import { getCurrentLanguage } from 'utils/LanguageUtils'
import { cn } from 'utils/StyleUtils'

import {
  tabs as consentTabs,
  generateCookiePreferences,
  googleAnalyticsHeader
} from './consent-config'
import { getFooterLinksFromPage } from './utils'

export const CookieConsent = window?.['CookieConsent']

export const getCookieConsent = () => {
  return CookieConsent?.get('CookieConsent')
    ? JSON.parse(CookieConsent?.get('CookieConsent') ?? '')
    : {}
}

const clearPreferenceStorageItems = () => {
  localStorage.removeItem('language')
  localStorage.removeItem('theme')
}

type Props = {
  isGoogleAnalyticsEnabled: boolean
}

const defaultCookieValue = {
  necessary: true,
  preferences: false,
  statistics: false
  // marketing: true // TODO: check with team on this
}

const Badge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="rounded-md relative top-1 text-gray bg-base-gray-soft size-5 text-xs justify-center items-center text-center inline-flex font-normal">
    {children}
  </span>
)

const CookieConsentDialog: React.FC<Props> = ({ isGoogleAnalyticsEnabled }) => {
  const { t } = useEcpTranslation()
  const [cookieValue, setCookieValue] =
    useState<Record<string, boolean>>(defaultCookieValue)
  const [selectedIndex, setSelectedIndex] = useState('consent')
  const { data: portalConfigData } = useContext(PortalConfigContext)
  const {
    data: pageData,
    setIsCookieConsentDialogOpen: setOpen,
    isCookieConsentDialogOpen: open
  } = usePageConfig()

  const preferences = generateCookiePreferences(
    portalConfigData?.portalConfig?.domain ?? ''
  )

  const footerLinks = getFooterLinksFromPage(pageData?.selectedPage)

  const tabs = consentTabs.map((tab) => ({
    ...tab,
    label: t(`cookie.tabs.${tab.id}`)
  }))

  useEffect(() => {
    if (!CookieConsent.hasConsent()) {
      setOpen(true)
    }
  }, [setOpen])

  const handleDenyNonEssential = () => {
    const cookiePayload = {
      necessary: true,
      preferences: false,
      statistics: false,
      method: 'explicit',
      utc: Date.now(),
      version: '1'
    }

    CookieConsent.set(
      'CookieConsent',
      JSON.stringify(cookiePayload),
      365 // expires in 1 year
    )
    // clear preference storage items if user has not allowed preferences
    clearPreferenceStorageItems()
    setOpen(false)
  }

  const handleAllowSelection = () => {
    const cookiePayload = {
      ...cookieValue,
      method: 'explicit',
      utc: Date.now(),
      version: '1'
    }

    CookieConsent.set(
      'CookieConsent',
      JSON.stringify(cookiePayload),
      365 // expires in 1 year
    )
    // clear preference storage items if user has not allowed preferences
    if (!cookieValue.preferences) {
      clearPreferenceStorageItems()
    }
    setOpen(false)
  }

  const handleAllowAll = () => {
    const cookiePayload = {
      ...Object.fromEntries(
        Object.keys(defaultCookieValue).map((type) => [type, true])
      ),
      method: 'explicit',
      utc: Date.now(),
      version: '1'
    }

    CookieConsent.set(
      'CookieConsent',
      JSON.stringify(cookiePayload),
      365 // expires in 1 year
    )
    setOpen(false)
  }

  const handleTabChange = (index: string) => {
    setSelectedIndex(index)
  }

  const handleCookieChange = (name: string, checked: boolean) => {
    setCookieValue((prevState) => ({ ...prevState, [name]: checked }))
  }

  if (!open) {
    return null
  }

  const links = footerLinks.length > 0 && (
    <>
      <p className="text-sm leading-6">{t('cookie.consent.more_info')}</p>
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap gap-2 text-sm">
          {(() => {
            const items = []

            footerLinks.forEach((link, index) => {
              const currentLanguage = getCurrentLanguage()
              const linkLabel = link.label?.[currentLanguage]
              const linkUrl = link.url?.[currentLanguage]

              if (linkLabel && linkUrl) {
                items.push(
                  <a
                    className="underline focus-visible:ring focus-visible:ring-offset-2 rounded-sm [text-underline-position:under] text-blue-600 hover:text-blue-800"
                    href={linkUrl}
                    key={`footer-link-${index}`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {linkLabel}
                  </a>
                )

                if (index < footerLinks.length - 1) {
                  items.push(
                    <span aria-hidden="true" key={`footer-sep-${index}`}>
                      &bull;
                    </span>
                  )
                }
              }
            })

            return items
          })()}
        </div>
      </div>
    </>
  )

  return (
    <DialogRoot open={open}>
      <DialogContent
        className="flex w-[90%] max-h-[90vh] md:max-w-2xl! sm:w-full flex-col gap-0 p-0 overflow-hidden"
        data-testid="cookie-consent"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only" />
        <DialogDescription className="sr-only" />

        <TabContainer
          className="h-full gap-4 flex flex-col"
          onValueChange={handleTabChange}
          value={selectedIndex}
        >
          <div className="flex px-4 pt-4">
            <TabList tabs={tabs} />
          </div>

          <div className="flex flex-col overflow-y-auto max-h-96 px-6">
            <TabContent
              className="gap-4 h-full"
              key={`tab-content-${selectedIndex}`}
              value={selectedIndex}
            >
              <div className="flex flex-col gap-4">
                {selectedIndex === 'consent' && (
                  <div className="flex flex-col gap-2.5">
                    <h2 className="text-2xl font-bold">
                      {t('cookie.consent.title')}
                    </h2>
                    <p className="text-sm leading-6">
                      {t('cookie.consent.description')}
                    </p>

                    {links}
                  </div>
                )}
                {selectedIndex === 'details' && (
                  <div className="flex flex-col gap-0">
                    {preferences.map((preference, index) => {
                      const filterdPreferences = preference.accordion.filter(
                        (item) => {
                          // Only show "Google Analytics" in cookie consent if it is enabled in 360 portal
                          if (item.header === googleAnalyticsHeader) {
                            return isGoogleAnalyticsEnabled
                          }

                          return true
                        }
                      )

                      return (
                        <div
                          className={cn('border-gray border-b', {
                            'border-0': index === preferences.length - 1
                          })}
                          key={preference.id}
                        >
                          <div className="flex justify-between w-full pt-3 pb-3">
                            <div className="flex gap-2 justify-between">
                              <h2 className="font-bold text-lg">
                                {t(`cookie.details.${preference.id}.title`)}
                              </h2>
                              <Badge>
                                {preference.accordion

                                  .filter((item) => {
                                    // Include "Google Analytics" for statistics count in cookie consent only
                                    // if it is enabled in 360 portal
                                    if (item.header === googleAnalyticsHeader) {
                                      return isGoogleAnalyticsEnabled
                                    }

                                    return true
                                  })
                                  // Shows the actual number of items not accordion items
                                  .reduce(
                                    (acc, item) => acc + item.details.length,
                                    0
                                  )}
                              </Badge>
                            </div>
                            {preference.id !== 'unclassified' &&
                              preference.id !== 'necessary' && (
                                <Switch
                                  checked={cookieValue[preference.id]}
                                  onCheckedChange={(checked) => {
                                    handleCookieChange(preference.id, checked)
                                  }}
                                  value={t(
                                    `cookie.${
                                      cookieValue[preference.id]
                                        ? 'switch_on'
                                        : 'switch_off'
                                    }`
                                  )}
                                />
                              )}
                          </div>
                          <p className="text-sm pb-2 pt-0">
                            {t(`cookie.details.${preference.id}.description`)}
                          </p>

                          {preference.accordion.length > 0 && (
                            <Accordion
                              className="last:mb-4 flex flex-col"
                              type="multiple"
                            >
                              {filterdPreferences.map((item, itemIndex) => (
                                <AccordionItem
                                  className="border-0"
                                  key={`${item.header}-${itemIndex}`}
                                  value={`${item.header}-${itemIndex}`}
                                >
                                  <AccordionTrigger className="text-sm tracking-wide font-semibold cursor-pointer py-1 break-all text-left">
                                    {item.header}
                                  </AccordionTrigger>
                                  {item?.details.map((detail, detailIndex) => (
                                    <AccordionContent
                                      className={cn(
                                        'gap-2 flex px-2 flex-col border-b py-3 border-gray',
                                        {
                                          'border-0':
                                            detailIndex ===
                                            item.details.length - 1
                                        }
                                      )}
                                      key={detail.title}
                                    >
                                      <div className="flex justify-between">
                                        <h4 className="font-bold text-xs">
                                          {t(detail.title)}
                                        </h4>
                                      </div>
                                      <p className="text-xs text-gray-light">
                                        {t(detail.description)}
                                      </p>
                                      <div className="flex gap-1 text-xs text-gray-light">
                                        <p>
                                          <span className="font-semibold text-gray">
                                            {t('cookie.expiry')}:{' '}
                                          </span>
                                          <span className="text-gray-light">
                                            {t(detail.expiry)},
                                          </span>
                                        </p>
                                        <p>
                                          <span className="font-semibold text-gray">
                                            {t('cookie.type')}:
                                          </span>{' '}
                                          <span className="text-gray-light">
                                            {t(detail.type)}
                                          </span>
                                        </p>
                                      </div>
                                    </AccordionContent>
                                  ))}
                                </AccordionItem>
                              ))}
                            </Accordion>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}

                {selectedIndex === 'about' && (
                  <div className="flex flex-col gap-4 pt-2">
                    <p className="text-sm leading-6">
                      {t('cookie.about.description')}
                    </p>

                    {links}
                  </div>
                )}
              </div>
            </TabContent>
          </div>
        </TabContainer>

        <DialogFooter className="flex w-full p-6 shrink-0 gap-4 md:gap-2 items-center justify-center flex-col overflow-hidden bg-slate-2 md:justify-end sm:flex-row">
          <Button
            className="w-full md:w-fit max-sm:min-w-40"
            onClick={handleDenyNonEssential}
            variant="secondary"
          >
            {t('cookie.deny_non_essential')}
          </Button>
          <Button
            className="w-full md:w-fit max-sm:min-w-40"
            onClick={handleAllowSelection}
            variant="secondary"
          >
            {t('cookie.allow_selection')}
          </Button>
          <Button
            className="w-full md:w-fit"
            onClick={handleAllowAll}
            variant="secondary"
          >
            {t('cookie.allow_all')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}

export default CookieConsentDialog
